import { createContext, useContext, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { MeResponse, UserProfile } from "@/types/api";

interface AuthContextValue {
  user: UserProfile | null;
  viewedAs: UserProfile | null;
  // The identity the app should behave as: the impersonated user when an admin
  // is impersonating, otherwise the real user. Use this for role-based routing
  // and gating; use `user` only for the real signed-in identity (header, etc).
  effectiveUser: UserProfile | null;
  impersonating: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<UserProfile | null>;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const me = useQuery({
    queryKey: ["me"],
    queryFn: async (): Promise<MeResponse | null> => {
      try {
        return await api.get<MeResponse>("/api/auth/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });

  const value: AuthContextValue = {
    user: me.data?.user ?? null,
    viewedAs: me.data?.viewedAs ?? null,
    effectiveUser: me.data?.viewedAs ?? me.data?.user ?? null,
    impersonating: me.data?.impersonating ?? false,
    loading: me.isLoading,
    refresh: async () => {
      const next = await qc.fetchQuery({
        queryKey: ["me"],
        queryFn: async (): Promise<MeResponse | null> => {
          try {
            return await api.get<MeResponse>("/api/auth/me");
          } catch (e) {
            if (e instanceof ApiError && e.status === 401) return null;
            throw e;
          }
        },
      });
      return next?.user ?? null;
    },
    signOut: async () => {
      await api.post("/api/auth/signout").catch(() => {});
      qc.setQueryData(["me"], null);
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
